import {
  Button,
  Card,
  Collapse,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  notification,
} from "antd";
import { FieldArray } from "formik";
import { get } from "lodash";
import { useState } from "react";
import { BiPlus } from "react-icons/bi";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";
import BenefitRow from "../Components/BenefitRow";
import ExcelUpload from "../Components/ExcelUpload";
import PricingInput from "../Components/PricingInput";

const BENEFIT_TYPES = [
  { value: "PRIMARY_CARE", label: "Soins courants" },
  { value: "SPECIALITY_CARE", label: "Soins spécialisés" },
];

const ConfigureBenefits = ({ values, setFieldValue, refetch }) => {
  const coverages = get(values, "insurancePlanCoverages") || [];
  const planPricingMode = get(values, "pricingMode");
  const zones = get(values, "zones") || [];
  const ageBrackets = get(values, "ageBrackets") || [];
  const regimes = get(values, "regimes") || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCovIndex, setDrawerCovIndex] = useState(null);
  const [form] = Form.useForm();

  const { data: benefitsData } = useQuery(
    graphql.queries.COVERAGE_BENEFITS,
    { variables: {} },
  );

  const [createBenefit, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_COVERAGE_BENEFIT,
    {
      refetchQueries: [
        { query: graphql.queries.COVERAGE_BENEFITS, variables: {} },
      ],
      awaitRefetchQueries: true,
    },
  );

  const [addMapping] = useMutation(graphql.mutations.CREATE_COVERAGE_MAPPING);
  const [deleteMapping] = useMutation(graphql.mutations.DELETE_COVERAGE_MAPPING);
  const [updateMapping] = useMutation(graphql.mutations.UPDATE_COVERAGE_MAPPING);
  const [createPricer] = useMutation(graphql.mutations.CREATE_PRICER);
  const [updatePricer] = useMutation(graphql.mutations.UPDATE_PRICER);

  const allBenefits = get(benefitsData, "coverageBenefits.data") || [];

  const handleCreateBenefit = () => {
    form.validateFields().then(async (formValues) => {
      const benefitId = cuid();
      const benefit = {
        id: benefitId,
        type: formValues.type,
        subtype: formValues.subtype || null,
        requirement: formValues.requirement || null,
        additionalInfo: formValues.additionalInfo || null,
        order: parseInt(formValues.order, 10) || 1,
      };

      try {
        await createBenefit({ variables: { data: benefit } });
        form.resetFields();
        setDrawerOpen(false);

        if (drawerCovIndex !== null) {
          await handleAddBenefit(drawerCovIndex, benefit);
        }
      } catch (err) {
        notification.error({ message: "Échec de la création de la prestation" });
      }
    });
  };

  const handleAddBenefit = async (covIndex, benefit) => {
    const coverage = coverages[covIndex];
    const mappingId = cuid();

    try {
      await addMapping({
        variables: {
          data: {
            id: mappingId,
            waitingDays: 0,
            insurancePlanCoverage: { id: coverage.id },
            coverage: { id: benefit.id },
          },
        },
      });
      const newMapping = {
        id: mappingId,
        waitingDays: 0,
        coverage: benefit,
        limits: [],
        supportingInformation: [],
        pricer: null,
      };
      const updatedMappings = [
        ...(get(coverage, "coverageMappings") || []),
        newMapping,
      ];
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings`,
        updatedMappings,
      );
      notification.success({ message: `Ajouté : ${benefit.type}` });
    } catch (err) {
      notification.error({ message: "Échec de l'ajout de la prestation" });
    }
  };

  const handleRemoveBenefit = async (covIndex, mapIndex) => {
    const coverage = coverages[covIndex];
    const mapping = (get(coverage, "coverageMappings") || [])[mapIndex];

    try {
      await deleteMapping({
        variables: { where: { id: mapping.id } },
      });
      const updatedMappings = (get(coverage, "coverageMappings") || []).filter(
        (_, i) => i !== mapIndex,
      );
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings`,
        updatedMappings,
      );
      notification.success({ message: "Prestation supprimée" });
    } catch (err) {
      notification.error({ message: "Échec de la suppression" });
    }
  };

  const handleUpdateMappingField = async (covIndex, mapIndex, field, value, localOnly) => {
    const mapping = (get(coverages[covIndex], "coverageMappings") || [])[mapIndex];
    if (!mapping) return;

    if (field === "_batch" && typeof value === "object") {
      for (const [k, v] of Object.entries(value)) {
        setFieldValue(
          `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.${k}`,
          v,
        );
      }
      try {
        await updateMapping({
          variables: {
            where: { id: mapping.id },
            data: value,
          },
        });
      } catch (err) {
        notification.error({ message: "Échec de la mise à jour" });
      }
      return;
    }

    setFieldValue(
      `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.${field}`,
      value,
    );

    if (localOnly) return;

    try {
      await updateMapping({
        variables: {
          where: { id: mapping.id },
          data: { [field]: value },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  const [updateCoverageObj] = useMutation(
    graphql.mutations.UPDATE_INSURANCE_PLAN_COVERAGE,
  );

  const ensurePricer = async (existingPricer, prices) => {
    const existingId = get(existingPricer, "id");
    if (existingId) {
      await updatePricer({
        variables: {
          where: { id: existingId },
          data: { prices },
        },
      });
      return { ...existingPricer, prices };
    }

    const newId = cuid();
    await createPricer({
      variables: {
        data: { id: newId, type: "db", name: "auto", prices },
      },
    });
    return { id: newId, type: "db", prices };
  };

  const handleUpdateBenefitPricer = async (covIndex, mapIndex, prices) => {
    const mapping = (get(coverages[covIndex], "coverageMappings") || [])[mapIndex];

    try {
      const pricer = await ensurePricer(get(mapping, "pricer"), prices);
      await updateMapping({
        variables: {
          where: { id: mapping.id },
          data: { pricer: { id: pricer.id } },
        },
      });
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.coverageMappings.${mapIndex}.pricer`,
        pricer,
      );
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour du tarif" });
    }
  };

  const handleUpdateCoveragePricer = async (covIndex, prices) => {
    const coverage = coverages[covIndex];

    try {
      const pricer = await ensurePricer(get(coverage, "pricer"), prices);
      await updateCoverageObj({
        variables: {
          where: { id: coverage.id },
          data: { pricer: { id: pricer.id } },
        },
      });
      setFieldValue(
        `insurancePlanCoverages.${covIndex}.pricer`,
        pricer,
      );
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour du tarif de la garantie" });
    }
  };

  const handleUpdateCoverageField = async (covIndex, field, value, localOnly) => {
    setFieldValue(`insurancePlanCoverages.${covIndex}.${field}`, value);

    if (localOnly) return;

    const coverage = coverages[covIndex];
    try {
      await updateCoverageObj({
        variables: {
          where: { id: coverage.id },
          data: { [field]: value },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  if (coverages.length === 0) {
    return (
      <div className="configure-step">
        <Empty description="Aucune garantie sélectionnée. Retournez à l'étape précédente." />
      </div>
    );
  }

  const collapseItems = coverages.map((coverage, covIndex) => {
    const category = get(coverage, "coverageCategory", {});
    const covPricingMode = get(coverage, "pricingMode", "PACKAGED");
    const mappings = get(coverage, "coverageMappings") || [];
    const selectedBenefitIds = mappings.map((m) => get(m, "coverage.id"));
    const availableBenefits = allBenefits.filter(
      (b) => !selectedBenefitIds.includes(b.id),
    );

    return {
      key: coverage.id,
      label: (
        <div className="d-flex justify--between" style={{ width: "100%" }}>
          <div
            className="d-flex"
            style={{ gap: "8px", alignItems: "center" }}
          >
            {category.icon && (
              <img
                src={category.icon}
                alt=""
                style={{ width: 24, height: 24 }}
              />
            )}
            <strong>{category.label || category.value}</strong>
            <Tag>{mappings.length} prestation(s)</Tag>
          </div>
          <Tag color={covPricingMode === "COMPOSABLE" ? "green" : "blue"}>
            {covPricingMode}
          </Tag>
        </div>
      ),
      children: (
        <div>
          {covPricingMode === "PACKAGED" && (
              <Card size="small" className="m-bottom-16">
                <div
                  className="d-flex"
                  style={{ gap: "12px", alignItems: "center" }}
                >
                  <strong>Prix de la garantie :</strong>
                  <PricingInput
                    value={get(coverage, "pricer.prices")}
                    zones={zones}
                    ageBrackets={ageBrackets}
                    regimes={regimes}
                    onChange={(prices) =>
                      handleUpdateCoveragePricer(covIndex, prices)
                    }
                  />
                </div>
              </Card>
            )}

          <div>
            <div className="d-flex justify--between m-bottom-12">
              <h4>Prestations</h4>
              <div className="d-flex" style={{ gap: "8px" }}>
                <Select
                  placeholder="Ajouter une prestation existante..."
                  style={{ width: 250 }}
                  showSearch
                  optionFilterProp="children"
                  value={null}
                  onChange={(benefitId) => {
                    const benefit = allBenefits.find(
                      (b) => b.id === benefitId,
                    );
                    if (benefit) handleAddBenefit(covIndex, benefit);
                  }}
                >
                  {availableBenefits.map((b) => (
                    <Select.Option key={b.id} value={b.id}>
                      {b.type}
                      {b.subtype ? ` - ${b.subtype}` : ""}
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  icon={<BiPlus size={16} />}
                  onClick={() => {
                    setDrawerCovIndex(covIndex);
                    setDrawerOpen(true);
                  }}
                >
                  Nouveau
                </Button>
              </div>
            </div>

            {mappings.length === 0 ? (
              <Empty
                description="Aucune prestation ajoutée. Sélectionnez dans la liste ou créez-en une nouvelle."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {mappings.map((mapping, mapIndex) => (
                  <BenefitRow
                    key={mapping.id}
                    mapping={mapping}
                    zones={zones}
                    ageBrackets={ageBrackets}
                    regimes={regimes}
                    showPricing={covPricingMode === "COMPOSABLE"}
                    onRemove={() => handleRemoveBenefit(covIndex, mapIndex)}
                    onFieldChange={(field, value, localOnly) =>
                      handleUpdateMappingField(covIndex, mapIndex, field, value, localOnly)
                    }
                    onPricerChange={(prices) =>
                      handleUpdateBenefitPricer(covIndex, mapIndex, prices)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ),
    };
  });

  return (
    <div className="configure-step">
      <div className="m-bottom-16">
        <ExcelUpload
          coverages={coverages}
          zones={zones}
          ageBrackets={ageBrackets}
          setFieldValue={setFieldValue}
          refetch={refetch}
        />
      </div>
      <Collapse
        items={collapseItems}
        defaultActiveKey={coverages.map((c) => c.id)}
      />

      <Drawer
        title="Nouvelle prestation"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={
          <Button
            type="primary"
            onClick={handleCreateBenefit}
            loading={creating}
          >
            Créer
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: "Le type est requis" }]}
          >
            <Select placeholder="Sélectionner le type">
              {BENEFIT_TYPES.map((t) => (
                <Select.Option key={t.value} value={t.value}>
                  {t.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="subtype" label="Sous-type">
            <Input placeholder="e.g. Orthodontics, Implants..." />
          </Form.Item>
          <Form.Item name="requirement" label="Condition">
            <Input placeholder="e.g. Referral required" />
          </Form.Item>
          <Form.Item name="additionalInfo" label="Informations complémentaires">
            <Input.TextArea rows={2} placeholder="Any extra details" />
          </Form.Item>
          <Form.Item name="order" label="Ordre d'affichage">
            <Input type="number" placeholder="1" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default ConfigureBenefits;
